// ============================================================================
// Approver History — GET /tort/approver/time-off-requests/history
//
// Returns paginated, sortable time-off request history for the approver
// history view, filtered by a date range.
//
// Required query params:
//  - date        → today's date (YYYY-MM-DD)
//  - startDate   → range start (YYYY-MM-DD)
//  - endDate     → range end   (YYYY-MM-DD)
//  - page        → 1-based page number
//  - perPage     → items per page
//
// Optional query params:
//  - sortBy      → 'empNUID' | 'shift' | 'rank'
//  - sortOrder   → 'ASC' | 'DESC'
//
// Required:
//  - x-nuid (header) → added automatically by the axios request interceptor
// ============================================================================

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import api from '../../../api/axios'
import endpoints from '../../../api/endpoints'
import { extractApiErrorMessage } from '../../../utils/apiError'

// ---------------------------------------------------------------------------
// Types — query params
// ---------------------------------------------------------------------------

/** Sortable columns accepted by the history API. */
export type ApproverHistorySortBy = 'empNUID' | 'shift' | 'rank' | 'dateRequested' | 'hoursRequested' | 'approver' | 'requestType' | 'status'

/** Sort direction accepted by the API. */
export type ApproverHistorySortOrder = 'ASC' | 'DESC'

/**
 * Query parameters for GET /tort/approver/time-off-requests/history.
 * All date strings must use YYYY-MM-DD format.
 */
export interface ApproverHistoryParams {
  /** Today's date — used as the reference date by the API. */
  date: string
  /** Inclusive start of the request date range. */
  startDate: string
  /** Inclusive end of the request date range. */
  endDate: string
  /** 1-based page number. */
  page: number
  /** Number of items per page. */
  perPage: number
  /** Column to sort by. Defaults to 'empNUID'. */
  sortBy?: ApproverHistorySortBy
  /** Sort direction. Defaults to 'ASC'. */
  sortOrder?: ApproverHistorySortOrder
}

// ---------------------------------------------------------------------------
// Types — API response
// ---------------------------------------------------------------------------

/**
 * A single history item as returned by the GET history endpoint.
 * Field names reflect the actual API contract.
 */
export interface ApproverHistoryItem {
  /** Employee NUID string, e.g. "S123456". */
  empNUID: string
  /** Employee display name, e.g. "Morgan, Jessie, Rose (X000233)". */
  employeeName: string
  /** Unique request identifier used for navigation to the detail page. */
  requestId: string
  /** Date(s) of the request, e.g. "06/30/2026" or "06/29/2026-07/01/2026". */
  toDate: string
  /** Requested hours as a string, e.g. "8". */
  hoursRequested: string
  /** Human-readable request type label, e.g. "Vacation (PTO/ETO)". */
  timeOffReqstType: string
  /** Human-readable status label, e.g. "Approved". */
  status: string
  /** Machine-readable status code used for status icons, e.g. "APPROVED". */
  statusCode: string
  /** Shift identifier, e.g. "Days". */
  shift: string
  /** Seniority rank, e.g. "1". */
  rank: string
  /** Name of the approver, e.g. "Smith, John". */
  approverName: string
}

/** Pagination metadata returned alongside history items. */
export interface ApproverHistoryMeta {
  pageNumber: number
  pageSize: number
  /** Total matching records across all pages. */
  total: number
  totalPages: number
}

/** Full response shape from GET /tort/approver/time-off-requests/history. */
export interface ApproverHistoryApiResponse {
  items: ApproverHistoryItem[]
  meta: ApproverHistoryMeta
}

// ---------------------------------------------------------------------------
// Redux state
// ---------------------------------------------------------------------------

export interface ApproverHistoryState {
  items: ApproverHistoryItem[]
  loading: boolean
  error: string | null
  currentPage: number
  pageSize: number
  /** Total records across all pages (from meta.total). */
  totalRecords: number
  totalPages: number
  sortBy?: ApproverHistorySortBy
  sortOrder?: ApproverHistorySortOrder
}

const initialState: ApproverHistoryState = {
  items: [],
  loading: false,
  error: null,
  currentPage: 1,
  pageSize: 20,
  totalRecords: 0,
  totalPages: 0,
}

// ---------------------------------------------------------------------------
// API helper
// ---------------------------------------------------------------------------

const fetchApproverHistory = async (
  params: ApproverHistoryParams,
  signal?: AbortSignal
): Promise<ApproverHistoryApiResponse> => {
  const response = await api.get(endpoints.approver.history, {
    params,
    signal,
  })

  return response.data as ApproverHistoryApiResponse
}

// ---------------------------------------------------------------------------
// Thunk
// ---------------------------------------------------------------------------

/**
 * Fetches processed time-off request history for the approver view.
 *
 * Dispatches pending → fulfilled | rejected lifecycle actions that update
 * `loading`, `items`, pagination state, and `error` in the slice.
 *
 * The `x-nuid` header is attached automatically by the axios request
 * interceptor — no manual header work is required at the call site.
 *
 * @param params - Query parameters for date range, pagination, and sorting.
 */
export const getApproverHistory = createAsyncThunk(
  'approverHistory/getApproverHistory',
  async (params: ApproverHistoryParams, { rejectWithValue, signal }) => {
    try {
      const data = await fetchApproverHistory(params, signal)

      return {
        items: data.items ?? [],
        currentPage: data.meta?.pageNumber ?? params.page,
        pageSize: data.meta?.pageSize ?? params.perPage,
        totalRecords: data.meta?.total ?? 0,
        totalPages: data.meta?.totalPages ?? 0,
      }
    } catch (error: unknown) {
      return rejectWithValue(
        extractApiErrorMessage(error, 'Failed to fetch approver history')
      )
    }
  }
)
// ---------------------------------------------------------------------------
// to print
// ---------------------------------------------------------------------------

export const getApproverHistoryForPrint = createAsyncThunk(
  'approverHistory/getApproverHistoryForPrint',
  async (params: ApproverHistoryParams, { rejectWithValue, signal }) => {
    try {
      return await fetchApproverHistory(params, signal)
    } catch (error: unknown) {
      return rejectWithValue(
        extractApiErrorMessage(
          error,
          'Failed to fetch approver history for print'
        )
      )
    }
  }
)

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const approverHistorySlice = createSlice({
  name: 'approverHistory',
  initialState,
  reducers: {
    /**
     * Update the active sort configuration.
     * @param action.payload.sortBy    - Column to sort by.
     * @param action.payload.sortOrder - ASC or DESC.
     */
    setApproverHistorySort: (
      state,
      action: {
        payload: { sortBy: ApproverHistorySortBy; sortOrder: ApproverHistorySortOrder }
      }
    ) => {
      state.sortBy = action.payload.sortBy
      state.sortOrder = action.payload.sortOrder
    },
    /** Reset state to initial values (e.g., on view unmount or logout). */
    clearApproverHistory: (state) => {
      Object.assign(state, initialState)
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getApproverHistory.pending, (state) => {
      state.loading = true
      state.error = null
    })
    builder.addCase(getApproverHistory.fulfilled, (state, action) => {
      state.loading = false
      state.items = action.payload.items
      state.currentPage = action.payload.currentPage
      state.pageSize = action.payload.pageSize
      state.totalRecords = action.payload.totalRecords
      state.totalPages = action.payload.totalPages
      state.error = null
    })
    builder.addCase(getApproverHistory.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload as string
    })
  },
})

export const { setApproverHistorySort, clearApproverHistory } =
  approverHistorySlice.actions
export default approverHistorySlice.reducer
